import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  displayAnalyticsValue,
  displayUtmValue,
  formatDuration,
  formatTimestamp,
  shortId,
} from '../utils/analyticsDisplay';

const SESSION_PAGE_SIZE = 40;
const EVENT_PAGE_SIZE = 60;
const DEFAULT_FILTERS = {
  search: '',
  from: '',
  to: '',
  referrerHost: '',
  utmSource: '',
  utmMedium: '',
  landingPath: '',
  eventType: '',
  trafficScope: 'human',
  returningOnly: false,
};

const AnalyticsTimelineWorkspace = ({
  visitors = [],
  recentEvents = [],
  recentAssetRequests = [],
  authToken = '',
}) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [sessionRows, setSessionRows] = useState([]);
  const [sessionCursor, setSessionCursor] = useState('');
  const [eventRows, setEventRows] = useState([]);
  const [eventCursor, setEventCursor] = useState('');
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [sessionsRemoteReady, setSessionsRemoteReady] = useState(false);
  const [eventsRemoteReady, setEventsRemoteReady] = useState(false);
  const [remoteMessage, setRemoteMessage] = useState('');
  const [exporting, setExporting] = useState(false);

  const deferredSearch = useDeferredValue(filters.search);
  const filterQuery = useMemo(
    () => ({
      ...filters,
      search: deferredSearch,
    }),
    [filters, deferredSearch]
  );
  const sessionFilterKey = useMemo(
    () => JSON.stringify(filterQuery),
    [filterQuery]
  );
  const eventFilterKey = useMemo(
    () => JSON.stringify({
      ...filterQuery,
      sessionId: selectedSessionId,
    }),
    [filterQuery, selectedSessionId]
  );

  const fallbackSessions = useMemo(() => buildFallbackSessions(visitors), [visitors]);
  const fallbackEvents = useMemo(
    () => buildFallbackEvents(recentEvents, recentAssetRequests, visitors),
    [recentAssetRequests, recentEvents, visitors]
  );

  const filteredFallbackSessions = useMemo(
    () => filterSessions(fallbackSessions, filterQuery),
    [fallbackSessions, filterQuery]
  );
  const filteredFallbackEvents = useMemo(
    () => filterEvents(fallbackEvents, filterQuery, selectedSessionId),
    [fallbackEvents, filterQuery, selectedSessionId]
  );

  useEffect(() => {
    if (!selectedSessionId) {
      return;
    }

    const currentSessions = sessionsRemoteReady ? sessionRows : filteredFallbackSessions;
    if (!currentSessions.some((session) => session.sessionId === selectedSessionId)) {
      setSelectedSessionId('');
    }
  }, [filteredFallbackSessions, selectedSessionId, sessionRows, sessionsRemoteReady]);

  useEffect(() => {
    let cancelled = false;

    const loadSessions = async () => {
      if (!authToken) {
        setSessionsRemoteReady(false);
        setSessionRows([]);
        setSessionCursor('');
        return;
      }

      setSessionsLoading(true);

      try {
        const payload = await fetchTimelineJson('sessions', {
          ...filterQuery,
          limit: SESSION_PAGE_SIZE,
        }, authToken);

        if (cancelled) return;

        setSessionRows(payload.items);
        setSessionCursor(payload.nextCursor || '');
        setSessionsRemoteReady(true);
        setRemoteMessage('');
      } catch (error) {
        if (cancelled) return;
        setSessionsRemoteReady(false);
        setSessionRows([]);
        setSessionCursor('');
        setRemoteMessage(error?.message || 'Timeline API unavailable. Showing dashboard payload only.');
      } finally {
        if (!cancelled) {
          setSessionsLoading(false);
        }
      }
    };

    void loadSessions();

    return () => {
      cancelled = true;
    };
  }, [authToken, sessionFilterKey, filterQuery]);

  useEffect(() => {
    let cancelled = false;

    const loadEvents = async () => {
      if (!authToken) {
        setEventsRemoteReady(false);
        setEventRows([]);
        setEventCursor('');
        return;
      }

      setEventsLoading(true);

      try {
        const payload = await fetchTimelineJson('events', {
          ...filterQuery,
          sessionId: selectedSessionId,
          limit: EVENT_PAGE_SIZE,
        }, authToken);

        if (cancelled) return;

        setEventRows(payload.items);
        setEventCursor(payload.nextCursor || '');
        setEventsRemoteReady(true);
        setRemoteMessage('');
      } catch (error) {
        if (cancelled) return;
        setEventsRemoteReady(false);
        setEventRows([]);
        setEventCursor('');
        setRemoteMessage(error?.message || 'Timeline API unavailable. Showing dashboard payload only.');
      } finally {
        if (!cancelled) {
          setEventsLoading(false);
        }
      }
    };

    void loadEvents();

    return () => {
      cancelled = true;
    };
  }, [authToken, eventFilterKey, filterQuery, selectedSessionId]);

  const displaySessions = sessionsRemoteReady ? sessionRows : filteredFallbackSessions;
  const displayEvents = eventsRemoteReady ? eventRows : filteredFallbackEvents;
  const selectedSession = displaySessions.find((session) => session.sessionId === selectedSessionId) || null;
  const sourceLabel = sessionsRemoteReady || eventsRemoteReady ? 'Live API' : 'Dashboard payload';
  const trafficScopeLabel = filters.trafficScope === 'human' ? 'Human only' : 'All traffic';

  const handleLoadMoreSessions = async () => {
    if (!authToken || !sessionCursor) {
      return;
    }

    setSessionsLoading(true);

    try {
      const payload = await fetchTimelineJson('sessions', {
        ...filterQuery,
        limit: SESSION_PAGE_SIZE,
        cursor: sessionCursor,
      }, authToken);

      setSessionRows((current) => [...current, ...payload.items]);
      setSessionCursor(payload.nextCursor || '');
      setSessionsRemoteReady(true);
    } catch (error) {
      setSessionsRemoteReady(false);
      setRemoteMessage(error?.message || 'Unable to load more sessions.');
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleLoadMoreEvents = async () => {
    if (!authToken || !eventCursor) {
      return;
    }

    setEventsLoading(true);

    try {
      const payload = await fetchTimelineJson('events', {
        ...filterQuery,
        sessionId: selectedSessionId,
        limit: EVENT_PAGE_SIZE,
        cursor: eventCursor,
      }, authToken);

      setEventRows((current) => [...current, ...payload.items]);
      setEventCursor(payload.nextCursor || '');
      setEventsRemoteReady(true);
    } catch (error) {
      setEventsRemoteReady(false);
      setRemoteMessage(error?.message || 'Unable to load more events.');
    } finally {
      setEventsLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (!authToken) {
      exportCsv(displayEvents, 'analytics-events.csv');
      return;
    }

    setExporting(true);

    try {
      const csv = await fetchTimelineCsv({
        ...filterQuery,
        sessionId: selectedSessionId,
      }, authToken);

      downloadTextFile(csv, 'analytics-events.csv', 'text/csv;charset=utf-8;');
    } catch {
      exportCsv(displayEvents, 'analytics-events.csv');
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="rounded-[34px] border border-[#e6d9c7] bg-white/94 shadow-[0_36px_110px_-68px_rgba(44,32,18,0.38)]">
      <div className="border-b border-[#e8dccd] bg-[linear-gradient(135deg,rgba(249,244,236,0.98),rgba(236,243,240,0.96)_48%,rgba(232,239,248,0.98)_100%)] px-5 py-5 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#8b6b42]">
              Timeline
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[#1b1611] sm:text-3xl">
              Chronological workspace
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-[#5f5448] sm:text-base">
              Wide tables for sessions and events. Leave the session unselected for a global stream, or click into one session for full detail.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <StatusPill label={sourceLabel} />
            <StatusPill label={trafficScopeLabel} />
            <StatusPill label={`${displaySessions.length.toLocaleString()} sessions`} />
            <StatusPill label={`${displayEvents.length.toLocaleString()} events`} />
          </div>
        </div>
      </div>

      <div className="sticky top-[82px] z-20 border-b border-[#e8dccd] bg-white/96 px-5 py-5 backdrop-blur sm:px-6 lg:px-8">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_repeat(4,minmax(0,0.72fr))]">
          <Field
            label="Search"
            value={filters.search}
            onChange={(value) => setFilters((current) => ({ ...current, search: value }))}
            placeholder="User, session, path, referrer, UTM"
            className="xl:col-span-1"
          />
          <Field
            label="From"
            value={filters.from}
            onChange={(value) => setFilters((current) => ({ ...current, from: value }))}
            type="date"
          />
          <Field
            label="To"
            value={filters.to}
            onChange={(value) => setFilters((current) => ({ ...current, to: value }))}
            type="date"
          />
          <Field
            label="Referrer host"
            value={filters.referrerHost}
            onChange={(value) => setFilters((current) => ({ ...current, referrerHost: value }))}
            placeholder="google.com"
          />
          <Field
            label="UTM source"
            value={filters.utmSource}
            onChange={(value) => setFilters((current) => ({ ...current, utmSource: value }))}
            placeholder="linkedin"
          />
          <Field
            label="UTM medium"
            value={filters.utmMedium}
            onChange={(value) => setFilters((current) => ({ ...current, utmMedium: value }))}
            placeholder="social"
          />
          <Field
            label="Landing path"
            value={filters.landingPath}
            onChange={(value) => setFilters((current) => ({ ...current, landingPath: value }))}
            placeholder="/projects"
          />
          <Field
            label="Event type"
            value={filters.eventType}
            onChange={(value) => setFilters((current) => ({ ...current, eventType: value }))}
            placeholder="resume_open"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <ToggleChip
              active={filters.trafficScope === 'human'}
              onClick={() => setFilters((current) => ({ ...current, trafficScope: 'human' }))}
              label="Human only"
            />
            <ToggleChip
              active={filters.trafficScope === 'all'}
              onClick={() => setFilters((current) => ({ ...current, trafficScope: 'all' }))}
              label="All traffic"
            />
            <ToggleChip
              active={filters.returningOnly}
              onClick={() => setFilters((current) => ({ ...current, returningOnly: !current.returningOnly }))}
              label="Returning only"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <SecondaryButton
              label="Clear filters"
              onClick={() => {
                setFilters(DEFAULT_FILTERS);
                setSelectedSessionId('');
              }}
            />
            <SecondaryButton
              label={exporting ? 'Exporting...' : 'Export CSV'}
              onClick={() => {
                void handleExportCsv();
              }}
              disabled={exporting}
            />
          </div>
        </div>

        {remoteMessage && (
          <div className="mt-3 rounded-[18px] border border-[#ead8c2] bg-[#fbf7f1] px-4 py-3 text-sm text-[#6f604f]">
            {remoteMessage}
          </div>
        )}
      </div>

      <div className="grid gap-0 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="border-b border-[#e8dccd] xl:border-b-0 xl:border-r">
          <TablePanel
            title="Sessions"
            subtitle="Chronological table"
            count={displaySessions.length}
            loading={sessionsLoading}
            emptyTitle="No sessions match"
            emptyDescription="Adjust the filters to restore the session table."
            footer={
              sessionsRemoteReady && sessionCursor ? (
                <LoadMoreButton
                  label={sessionsLoading ? 'Loading...' : 'Load more sessions'}
                  onClick={() => {
                    void handleLoadMoreSessions();
                  }}
                  disabled={sessionsLoading}
                />
              ) : null
            }
          >
            <div className="max-h-[72vh] overflow-auto">
              <table className="min-w-[1480px] border-separate border-spacing-0 text-left">
                <thead className="sticky top-0 z-10 bg-[#f8f3eb]">
                  <tr>
                    {[
                      'Time',
                      'Visitor',
                      'Session',
                      'Duration',
                      'Pages',
                      'Events',
                      'Entry',
                      'Last path',
                      'Source',
                      'Traffic',
                      'Location',
                      'Device',
                    ].map((label) => (
                      <th
                        key={label}
                        className="border-b border-[#e6d9c8] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#786959]"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displaySessions.map((session) => {
                    const selected = session.sessionId === selectedSessionId;
                    return (
                      <tr
                        key={session.sessionId}
                        onClick={() => setSelectedSessionId((current) => (current === session.sessionId ? '' : session.sessionId))}
                        className={`cursor-pointer transition ${
                          selected ? 'bg-[#eef5f8]' : 'hover:bg-[#faf6ef]'
                        }`}
                        aria-selected={selected}
                      >
                        <Cell mono sticky selected={selected}>{formatTimestamp(session.lastSeenAt || session.firstSeenAt)}</Cell>
                        <Cell mono selected={selected}>{shortId(session.userId)}</Cell>
                        <Cell mono selected={selected} className="text-[#345469]">
                          {shortId(session.sessionId)}
                        </Cell>
                        <Cell selected={selected}>{formatDuration(session.durationMs)}</Cell>
                        <Cell selected={selected}>{Number(session.pageViews || 0).toLocaleString()}</Cell>
                        <Cell selected={selected}>{Number(resolveSessionEventCount(session)).toLocaleString()}</Cell>
                        <Cell mono selected={selected}>{displayAnalyticsValue(session.entryPath, '/')}</Cell>
                        <Cell mono selected={selected}>{displayAnalyticsValue(session.lastPath || session.entryPath, '/')}</Cell>
                        <Cell selected={selected}>{formatSource(session)}</Cell>
                        <Cell selected={selected}>{session.trafficClass || 'human'}</Cell>
                        <Cell selected={selected}>{formatLocation(session.location)}</Cell>
                        <Cell selected={selected}>{formatDevice(session.profile)}</Cell>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TablePanel>
        </div>

        <div>
          <TablePanel
            title={selectedSession ? 'Session events' : 'Event stream'}
            subtitle={selectedSession ? shortId(selectedSession.sessionId) : 'Filtered stream'}
            count={displayEvents.length}
            loading={eventsLoading}
            emptyTitle="No events match"
            emptyDescription="Try widening the source, date, or event filters."
            actions={
              selectedSession ? (
                <SecondaryButton label="Show global stream" onClick={() => setSelectedSessionId('')} />
              ) : null
            }
            footer={
              eventsRemoteReady && eventCursor ? (
                <LoadMoreButton
                  label={eventsLoading ? 'Loading...' : 'Load more events'}
                  onClick={() => {
                    void handleLoadMoreEvents();
                  }}
                  disabled={eventsLoading}
                />
              ) : null
            }
          >
            <div className="max-h-[72vh] overflow-auto">
              <table className="min-w-[1520px] border-separate border-spacing-0 text-left">
                <thead className="sticky top-0 z-10 bg-[#f8f3eb]">
                  <tr>
                    {[
                      'Time',
                      'Event',
                      'Visitor',
                      'Session',
                      'Path',
                      'Source',
                      'Details',
                      'Location',
                      'Network',
                      'Traffic',
                    ].map((label) => (
                      <th
                        key={label}
                        className="border-b border-[#e6d9c8] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#786959]"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayEvents.map((event) => (
                    <tr key={event.eventId} className="transition hover:bg-[#faf6ef]">
                      <Cell mono sticky>{formatTimestamp(event.ts)}</Cell>
                      <Cell>{humanizeEventType(event.eventType)}</Cell>
                      <Cell mono>{shortId(event.userId)}</Cell>
                      <Cell mono className="text-[#345469]">
                        {shortId(event.sessionId)}
                      </Cell>
                      <Cell mono>{displayAnalyticsValue(event.path, '/')}</Cell>
                      <Cell>{formatSource(event)}</Cell>
                      <Cell>{formatEventDetails(event)}</Cell>
                      <Cell>{formatLocation(event.location)}</Cell>
                      <Cell>{formatNetwork(event.network)}</Cell>
                      <Cell>{event.trafficClass || 'human'}</Cell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TablePanel>
        </div>
      </div>
    </section>
  );
};

const TablePanel = ({
  title,
  subtitle,
  count,
  loading,
  emptyTitle,
  emptyDescription,
  actions,
  footer,
  children,
}) => {
  const hasRows = count > 0;

  return (
    <section className="h-full">
      <div className="flex items-start justify-between gap-4 border-b border-[#e8dccd] px-5 py-4 sm:px-6 lg:px-7">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8b6b42]">{subtitle}</p>
          <h3 className="mt-1 text-lg font-semibold text-[#1b1611]">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill label={loading ? 'Loading' : Number(count || 0).toLocaleString()} />
          {actions}
        </div>
      </div>

      {!hasRows && !loading ? (
        <div className="px-5 py-10 sm:px-6 lg:px-7">
          <div className="rounded-[22px] border border-dashed border-[#dccab4] bg-[#fbf8f3] px-5 py-9">
            <p className="text-sm font-semibold text-[#2c241d]">{emptyTitle}</p>
            <p className="mt-1 text-sm leading-6 text-[#756759]">{emptyDescription}</p>
          </div>
        </div>
      ) : (
        <>
          {children}
          {footer && (
            <div className="border-t border-[#e8dccd] px-5 py-4 sm:px-6 lg:px-7">
              {footer}
            </div>
          )}
        </>
      )}
    </section>
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text', className = '' }) => (
  <label className={`block ${className}`.trim()}>
    <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8b7a68]">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full rounded-[16px] border border-[#ddcfbf] bg-white px-4 py-3 text-sm text-[#241d16] outline-none transition placeholder:text-[#a89b8b] focus:border-[#9f7c53] focus:shadow-[0_0_0_3px_rgba(159,124,83,0.08)]"
    />
  </label>
);

const ToggleChip = ({ active, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-semibold transition hover:-translate-y-0.5 ${
      active
        ? 'border-[#1f4f67] bg-[#1f4f67] text-white'
        : 'border-[#d8c8b2] bg-[#faf5ee] text-[#69523a]'
    }`}
  >
    {label}
  </button>
);

const SecondaryButton = ({ label, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-full border border-[#d8c8b2] bg-white/80 px-4 py-2 text-sm font-semibold text-[#5d4b35] transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
  >
    {label}
  </button>
);

const LoadMoreButton = ({ label, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-full bg-[#1f4f67] px-4 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#184155] disabled:cursor-not-allowed disabled:opacity-60"
  >
    {label}
  </button>
);

const StatusPill = ({ label }) => (
  <span className="rounded-full border border-[#eadfce] bg-[#f8f2e8] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#6b563f]">
    {label}
  </span>
);

const Cell = ({ children, mono = false, sticky = false, selected = false, className = '' }) => (
  <td
    className={`border-b border-[#ece1d3] px-4 py-3 align-top text-sm text-[#2d241d] ${
      mono ? 'font-mono text-[12px]' : ''
    } ${sticky ? `sticky left-0 z-[1] ${selected ? 'bg-[#eef5f8]' : 'bg-white'}` : ''} ${className}`.trim()}
  >
    {children}
  </td>
);

const fetchTimelineJson = async (resource, query, token) => {
  const response = await fetch(`/api/analytics/${resource}?${buildTimelineQueryString(query)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`${resource} request failed (${response.status})`);
  }

  const body = await response.json().catch(() => ({}));
  return normalizeTimelinePayload(body);
};

const fetchTimelineCsv = async (query, token) => {
  const response = await fetch(`/api/analytics/events?${buildTimelineQueryString({
    ...query,
    format: 'csv',
  })}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`csv request failed (${response.status})`);
  }

  return response.text();
};

const normalizeTimelinePayload = (body) => ({
  items: Array.isArray(body?.items)
    ? body.items
    : Array.isArray(body?.sessions)
      ? body.sessions
      : Array.isArray(body?.events)
        ? body.events
        : [],
  nextCursor: typeof body?.nextCursor === 'string' ? body.nextCursor : '',
});

const buildTimelineQueryString = (query) => {
  const params = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (typeof value === 'boolean') {
      if (value) {
        params.set(key, 'true');
      }
      return;
    }

    params.set(key, String(value));
  });

  return params.toString();
};

const buildFallbackSessions = (visitors) =>
  visitors
    .flatMap((visitor) =>
      (Array.isArray(visitor?.recentSessions) ? visitor.recentSessions : []).map((session) => ({
        sessionId: session.sessionId || `${visitor.userId}-${session.firstSeenAt}`,
        userId: visitor.userId,
        firstSeenAt: session.firstSeenAt || visitor.firstSeenAt || session.lastSeenAt || 0,
        lastSeenAt: session.lastSeenAt || session.firstSeenAt || visitor.lastSeenAt || 0,
        durationMs: session.durationMs || 0,
        pageViews: session.pageViews || 0,
        eventCount: session.eventCount || 0,
        humanEventCount: session.humanEventCount || 0,
        entryPath: session.entryPath || '/',
        lastPath: session.lastPath || session.entryPath || '/',
        trafficClass: visitor.trafficClass || 'human',
        location: session.location || visitor.location || {},
        network: session.network || visitor.network || {},
        profile: session.profile || visitor.profile || {},
        attribution: visitor.latestAttribution || visitor.firstAttribution || {},
        firstTouchAttribution: visitor.firstAttribution || {},
        latestTouchAttribution: visitor.latestAttribution || visitor.firstAttribution || {},
        visitorSessionCount: Number(visitor.sessionCount || 0),
        isReturningVisitor: Number(visitor.sessionCount || 0) > 1,
      }))
    )
    .sort((a, b) => Number(b.lastSeenAt || 0) - Number(a.lastSeenAt || 0));

const buildFallbackEvents = (recentEvents, recentAssetRequests, visitors) => {
  const visitorLookup = new Map(visitors.map((visitor) => [visitor.userId, visitor]));

  const intentEvents = (Array.isArray(recentEvents) ? recentEvents : []).map((event, index) => {
    const visitor = visitorLookup.get(event.userId);
    return {
      eventId: event.eventId || `event-${event.ts || index}`,
      ts: Number(event.ts || 0),
      eventType: event.eventType || 'event',
      userId: event.userId || visitor?.userId || '',
      sessionId: event.sessionId || '',
      path: event.path || '',
      trafficClass: event.trafficClass || visitor?.trafficClass || 'human',
      source: event.source || 'client_event',
      details: event.details || {},
      attribution: event.attribution || visitor?.latestAttribution || visitor?.firstAttribution || {},
      location: event.location || visitor?.location || {},
      network: event.network || visitor?.network || {},
      profile: event.profile || visitor?.profile || {},
      visitorSessionCount: Number(visitor?.sessionCount || 0),
    };
  });

  const assetEvents = (Array.isArray(recentAssetRequests) ? recentAssetRequests : []).map((request, index) => ({
    eventId: request.eventId || `asset-${request.ts || index}`,
    ts: Number(request.ts || 0),
    eventType: 'asset_request',
    userId: request.userId || '',
    sessionId: request.sessionId || '',
    path: request.path || '',
    trafficClass: request.trafficClass || 'human',
    source: 'asset_request',
    details: {
      assetType: request.assetType || 'asset',
      label: request.label || '',
    },
    attribution: {
      referrerHost: request.referrerHost || 'direct',
      utmSource: '',
      utmMedium: '',
      landingPath: request.path || '',
    },
    location: request.location || {},
    network: request.network || {},
    profile: {},
    visitorSessionCount: 0,
  }));

  return [...intentEvents, ...assetEvents].sort((a, b) => Number(b.ts || 0) - Number(a.ts || 0));
};

const filterSessions = (sessions, filters) =>
  sessions.filter((session) => {
    if (!withinDateRange(session.lastSeenAt || session.firstSeenAt, filters.from, filters.to)) {
      return false;
    }

    if (filters.trafficScope === 'human' && session.trafficClass && session.trafficClass !== 'human') {
      return false;
    }

    if (filters.returningOnly && Number(session.visitorSessionCount || 0) <= 1) {
      return false;
    }

    if (!matchesText(session, filters.search)) {
      return false;
    }

    if (!matchesField(session.attribution?.referrerHost || session.referrerHost, filters.referrerHost)) return false;
    if (!matchesField(session.attribution?.utmSource || session.utmSource, filters.utmSource)) return false;
    if (!matchesField(session.attribution?.utmMedium || session.utmMedium, filters.utmMedium)) return false;
    if (!matchesField(session.attribution?.landingPath || session.landingPath, filters.landingPath)) return false;

    return true;
  });

const filterEvents = (events, filters, selectedSessionId) =>
  events.filter((event) => {
    if (!withinDateRange(event.ts, filters.from, filters.to)) {
      return false;
    }

    if (filters.trafficScope === 'human' && event.trafficClass && event.trafficClass !== 'human') {
      return false;
    }

    if (filters.eventType && event.eventType !== filters.eventType) {
      return false;
    }

    if (filters.returningOnly && Number(event.visitorSessionCount || 0) <= 1) {
      return false;
    }

    if (selectedSessionId && event.sessionId !== selectedSessionId) {
      return false;
    }

    if (!matchesText(event, filters.search)) {
      return false;
    }

    if (!matchesField(event.attribution?.referrerHost, filters.referrerHost)) return false;
    if (!matchesField(event.attribution?.utmSource, filters.utmSource)) return false;
    if (!matchesField(event.attribution?.utmMedium, filters.utmMedium)) return false;
    if (!matchesField(event.attribution?.landingPath, filters.landingPath)) return false;

    return true;
  });

const matchesText = (row, search) => {
  const query = String(search || '').trim().toLowerCase();
  if (!query) return true;

  const haystack = [
    row.eventType,
    row.userId,
    row.sessionId,
    row.path,
    row.source,
    row.trafficClass,
    row.details && JSON.stringify(row.details),
    row.attribution && JSON.stringify(row.attribution),
    row.location && JSON.stringify(row.location),
    row.network && JSON.stringify(row.network),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(query);
};

const matchesField = (value, expected) => {
  const normalizedExpected = displayAnalyticsValue(expected, '').toLowerCase().trim();
  if (!normalizedExpected) return true;

  const normalizedValue = displayAnalyticsValue(value, '').toLowerCase().trim();
  return normalizedValue.includes(normalizedExpected);
};

const withinDateRange = (value, from, to) => {
  const ts = Number(value || 0);
  if (!Number.isFinite(ts) || ts <= 0) {
    return true;
  }

  if (from) {
    const fromTs = Date.parse(`${from}T00:00:00`);
    if (Number.isFinite(fromTs) && ts < fromTs) {
      return false;
    }
  }

  if (to) {
    const toTs = Date.parse(`${to}T23:59:59.999`);
    if (Number.isFinite(toTs) && ts > toTs) {
      return false;
    }
  }

  return true;
};

const resolveSessionEventCount = (session) =>
  Number(session.humanEventCount || session.eventCount || 0);

const humanizeEventType = (eventType) => {
  switch (eventType) {
    case 'resume_open':
      return 'Resume open';
    case 'social_click':
      return 'Social click';
    case 'contact_click':
      return 'Contact click';
    case 'paper_pdf_open':
      return 'PDF open';
    case 'paper_code_click':
      return 'Code click';
    case 'external_link_click':
      return 'Outbound link';
    case 'asset_request':
      return 'Asset hit';
    default: {
      const normalized = displayAnalyticsValue(eventType, 'event');
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }
  }
};

const formatSource = (row) => {
  const attribution = normalizeAttribution(
    row.latestTouchAttribution || row.attribution || row.firstTouchAttribution
  );
  const referrerHost = displayAnalyticsValue(row.referrerHost || attribution.referrerHost, 'direct');
  const utmSource = displayUtmValue(row.utmSource || attribution.utmSource, '');
  const utmMedium = displayUtmValue(row.utmMedium || attribution.utmMedium, '');
  const parts = [referrerHost];
  if (utmSource) parts.push(utmSource);
  if (utmMedium) parts.push(utmMedium);
  return parts.filter(Boolean).join(' · ') || 'direct';
};

const formatLocation = (location) => {
  const parts = [location?.city, location?.region, location?.country]
    .map((value) => displayAnalyticsValue(value, ''))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : 'Unknown';
};

const formatDevice = (profile) => {
  const device = displayAnalyticsValue(profile?.deviceType, 'Unknown device');
  const browser = displayAnalyticsValue(profile?.browserName, '');
  return browser ? `${device} / ${browser}` : device;
};

const formatNetwork = (network) => {
  const isp = displayAnalyticsValue(network?.isp, 'Unknown ISP');
  const asn = displayAnalyticsValue(network?.asn, '');
  return asn ? `${isp} / ${asn}` : isp;
};

const formatEventDetails = (event) => {
  if (event.source === 'asset_request') {
    return `${displayAnalyticsValue(event.details?.assetType, 'asset')} ${displayAnalyticsValue(event.details?.label, '')}`.trim();
  }

  if (!event.details || typeof event.details !== 'object') {
    return '—';
  }

  const parts = [];
  if (event.details.resumeVersion) parts.push(`resume ${displayAnalyticsValue(event.details.resumeVersion, '')}`);
  if (event.details.platform) parts.push(displayAnalyticsValue(event.details.platform, ''));
  if (event.details.method) parts.push(displayAnalyticsValue(event.details.method, ''));
  if (event.details.paperSlug) parts.push(displayAnalyticsValue(event.details.paperSlug, ''));
  if (event.details.surface) parts.push(displayAnalyticsValue(event.details.surface, ''));
  if (event.details.label) parts.push(displayAnalyticsValue(event.details.label, ''));

  return parts.filter(Boolean).join(' · ') || '—';
};

const exportCsv = (rows, filename) => {
  const headers = [
    'ts',
    'eventType',
    'userId',
    'sessionId',
    'path',
    'trafficClass',
    'source',
    'referrerHost',
    'utmSource',
    'utmMedium',
    'landingPath',
    'location',
    'network',
    'details',
  ];

  const lines = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((header) => csvValue(extractCsvField(row, header)))
        .join(',')
    ),
  ];

  downloadTextFile(lines.join('\n'), filename, 'text/csv;charset=utf-8;');
};

const downloadTextFile = (text, filename, mimeType) => {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const extractCsvField = (row, header) => {
  switch (header) {
    case 'ts':
      return formatTimestamp(row.ts);
    case 'eventType':
      return row.eventType;
    case 'userId':
      return row.userId;
    case 'sessionId':
      return row.sessionId;
    case 'path':
      return row.path;
    case 'trafficClass':
      return row.trafficClass;
    case 'source':
      return row.source;
    case 'referrerHost':
      return row.attribution?.referrerHost || row.referrerHost || '';
    case 'utmSource':
      return row.attribution?.utmSource || row.utmSource || '';
    case 'utmMedium':
      return row.attribution?.utmMedium || row.utmMedium || '';
    case 'landingPath':
      return row.attribution?.landingPath || row.landingPath || '';
    case 'location':
      return formatLocation(row.location);
    case 'network':
      return formatNetwork(row.network);
    case 'details':
      return formatEventDetails(row);
    default:
      return '';
  }
};

const csvValue = (value) => {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
};

const normalizeAttribution = (value) => (
  value && typeof value === 'object'
    ? value
    : {}
);

export default AnalyticsTimelineWorkspace;
